import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Fieldset,
  GridItem,
  HStack,
  Input,
  Portal,
  SimpleGrid,
  Skeleton,
  Switch,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { getFragmentData } from '@client/graphql/types';
import { BannerPlatform } from '@client/graphql/types/graphql';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { toaster } from '@packages/provider/module/chakra-ui/components/ui/toaster';
import { useTranslate } from '@tolgee/react';
import moment from 'moment-timezone';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TopBannerFragment } from '../../logics/top-banner/top-banner-fragment';
import { useDeleteTopBanners } from '../../logics/top-banner/use-delete-top-banner';
import { useTopBanner } from '../../logics/top-banner/use-top-banner';
import { useUpdateTopBanners } from '../../logics/top-banner/use-update-top-banner';
import BannerPlatformSelection from './banner-platform-selection';
import { ColorPicker } from './color-picker';

interface FormValues {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
  platforms: BannerPlatform[];
  backgroundColor: string;
}

export default function TopBannerUpdateModal(props: { id: string }) {
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();
  const { data, loading } = useTopBanner(props.id);
  const [update, { loading: updateLoading, reset: updateReset }] = useUpdateTopBanners();
  const [deleteTopBanner, { loading: deleteLoading }] = useDeleteTopBanners();
  const [open, setOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const {
    watch,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      description: '',
      startTime: '',
      endTime: '',
      isActive: true,
      platforms: [],
      backgroundColor: '#FFFFFF',
    },
  });

  useEffect(() => {
    if (data?.topBanner) {
      const topBannerData = getFragmentData(TopBannerFragment, data.topBanner);

      reset({
        title: topBannerData.title,
        description: topBannerData.description ?? '',
        startTime: moment.tz(topBannerData.startTime, company.settings.timezone).format('YYYY-MM-DDTHH:mm'),
        endTime: moment.tz(topBannerData.endTime, company.settings.timezone).format('YYYY-MM-DDTHH:mm'),
        isActive: topBannerData.isActive,
        platforms: topBannerData.platforms,
        backgroundColor: topBannerData.backgroundColor,
      });
      updateReset();
    }
  }, [data?.topBanner, reset, updateReset, open, company.settings.timezone]);

  const onSubmit = () => {
    update({
      variables: {
        id: props.id,
        input: {
          title: watch('title'),
          description: watch('description'),
          startTime: watch('startTime'),
          endTime: watch('endTime'),
          isActive: watch('isActive'),
          platforms: watch('platforms'),
          backgroundColor: watch('backgroundColor'),
        },
      },
    })
      .then(() => {
        toaster.create({
          description: t('top-banner.update.alert', 'Update top banner successful'),
          type: 'info',
        });
        setOpen(false);
      })
      .catch(() => {
        toaster.create({
          description: t('top-banner.update.alert.failed', 'Update top banner failed'),
          type: 'error',
        });
      });
  };

  const handleDelete = () => {
    deleteTopBanner({
      variables: {
        id: props.id,
      },
    })
      .then(() => {
        toaster.create({
          description: t('top-banner.delete.alert', 'Delete top banner successful'),
          type: 'info',
        });
        setOpen(false);
        setShowDeleteConfirm(false);
      })
      .catch(() => {
        toaster.create({
          description: t('top-banner.delete.alert.failed', 'Delete top banner failed'),
          type: 'error',
        });
      });
  };

  const handleShowDelete = () => {
    setOpen(false);
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setOpen(true);
    setShowDeleteConfirm(false);
  };

  if (loading) {
    return (
      <Dialog.Root
        lazyMount
        open={open}
        onOpenChange={(e) => {
          setOpen(e.open);
        }}
        placement={'center'}
      >
        <Dialog.Trigger asChild>
          <Button variant={'surface'} p={2} bgColor={'white'}>
            {t('happy-hour.button.edit', 'Edit')}
          </Button>
        </Dialog.Trigger>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title fontSize={'xl'}>{t('top-banner.dialog.update.title', 'Update Top Banner')}</Dialog.Title>
              </Dialog.Header>

              <Dialog.Body>
                <Fieldset.Root size="lg" maxW="md">
                  <Fieldset.Content>
                    <Field.Root>
                      <Field.Label>{t('top-banner.create.title', 'Title')}</Field.Label>
                      <Skeleton height="40px" />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('top-banner.create.description', 'Description')}</Field.Label>
                      <Skeleton height="40px" />
                    </Field.Root>

                    <SimpleGrid gap={2} columns={2}>
                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('top-banner.create.start-time', 'Start Time')}</Field.Label>
                          <Skeleton height="40px" />
                        </Field.Root>
                      </GridItem>

                      <GridItem>
                        <Field.Root>
                          <Field.Label>{t('top-banner.create.end-time', 'End Time')}</Field.Label>
                          <Skeleton height="40px" />
                        </Field.Root>
                      </GridItem>
                    </SimpleGrid>

                    <Field.Root>
                      <Field.Label>{t('top-banner.update.platforms', 'Platforms')}</Field.Label>
                      <Skeleton height="40px" />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('top-banner.update.is-active', 'Active Status')}</Field.Label>
                      <Skeleton height="24px" width="100px" />
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('top-banner.update.background-color', 'Background Color')}</Field.Label>
                      <Skeleton height="40px" />
                    </Field.Root>
                  </Fieldset.Content>
                </Fieldset.Root>
              </Dialog.Body>

              <Dialog.Footer>
                <HStack justifyContent={'space-between'} w={'full'}>
                  <Skeleton height="40px" width="100px" />
                  <HStack>
                    <Skeleton height="40px" width="100px" />
                    <Skeleton height="40px" width="100px" />
                  </HStack>
                </HStack>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    );
  }

  const FieldSet = (
    <Fieldset.Root size="lg" maxW="md">
      <Fieldset.Content>
        <Field.Root>
          <Field.Label>{t('top-banner.create.title', 'Title')}</Field.Label>
          <Input
            borderColor={'gray.300'}
            rounded="sm"
            {...register('title', {
              required: t('top-banner.create.title-required', 'Title is required'),
              maxLength: {
                value: 200,
                message: t('top-banner.create.title-max-length', 'Title must be less than 200 characters'),
              },
            })}
          />
          <Field.HelperText>
            {watch('title').length || 0}/200 {t('common.characters', 'characters')}
          </Field.HelperText>
          {errors.title && (
            <Text color="red.500" fontSize="sm">
              {errors.title.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('top-banner.create.description', 'Description')}</Field.Label>
          <Textarea
            borderColor={'gray.300'}
            rounded="sm"
            minH="100px"
            {...register('description', {
              required: t('top-banner.create.description-required', 'Description is required'),
            })}
          />
          {errors.description && (
            <Text color="red.500" fontSize="sm">
              {errors.description.message}
            </Text>
          )}
        </Field.Root>

        <SimpleGrid gap={2} columns={2}>
          <GridItem>
            <Field.Root>
              <Field.Label>{t('top-banner.create.start-time', 'Start Time')}</Field.Label>
              <Input
                type="datetime-local"
                borderColor={'gray.300'}
                rounded="sm"
                {...register('startTime', {
                  required: t('top-banner.update.start-time-required', 'Start time is required'),
                  validate: (value) => {
                    const endTime = watch('endTime');
                    if (value >= endTime) {
                      return t('top-banner.update.start-time-before-end', 'Start time must be before end time');
                    }
                    return true;
                  },
                })}
              />
              {errors.startTime && (
                <Text color="red.500" fontSize="sm">
                  {errors.startTime.message}
                </Text>
              )}
            </Field.Root>
          </GridItem>

          <GridItem>
            <Field.Root>
              <Field.Label>{t('top-banner.create.end-time', 'End Time')}</Field.Label>
              <Input
                type="datetime-local"
                borderColor={'gray.300'}
                rounded="sm"
                {...register('endTime', {
                  required: t('top-banner.update.end-time-required', 'End time is required'),
                  validate: (value) => {
                    const startTime = watch('startTime');
                    if (value <= startTime) {
                      return t('top-banner.update.end-time-after-start', 'End time must be after start time');
                    }
                    return true;
                  },
                })}
              />
              {errors.endTime && (
                <Text color="red.500" fontSize="sm">
                  {errors.endTime.message}
                </Text>
              )}
            </Field.Root>
          </GridItem>
        </SimpleGrid>

        <Field.Root>
          <Field.Label>{t('top-banner.update.platforms', 'Platforms')}</Field.Label>
          <input
            type="hidden"
            {...register('platforms', {
              required: t('top-banner.update.platforms-requred', 'At least one platform is required'),
              validate: (value) =>
                value.length > 0 || t('top-banner.update.platforms-required', 'At least one platform is required'),
            })}
          />
          <BannerPlatformSelection
            platform={watch('platforms')}
            onChange={(value: BannerPlatform[]) => {
              setValue('platforms', value);
            }}
          />
          {errors.platforms && (
            <Text color="red.500" fontSize="sm">
              {errors.platforms.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('top-banner.update.is-active', 'Active Status')}</Field.Label>
          <Switch.Root
            checked={watch('isActive')}
            onCheckedChange={({ checked }) => {
              setValue('isActive', checked);
            }}
          >
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
          {errors.isActive && (
            <Text color="red.500" fontSize="sm">
              {errors.isActive.message}
            </Text>
          )}
        </Field.Root>

        <Field.Root>
          <Field.Label>{t('top-banner.update.background-color', 'Background Color')}</Field.Label>
          <input
            type="hidden"
            {...register('backgroundColor', {
              required: t('top-banner.update.background-color-required', 'Background color is required'),
              validate: (value) =>
                value.trim() !== '' || t('top-banner.update.background-color-required', 'Background color is required'),
            })}
          />
          <ColorPicker
            selectedColor={watch('backgroundColor')}
            onColorSelect={(color) => {
              setValue('backgroundColor', color);
            }}
          />
          {errors.backgroundColor && (
            <Text color="red.500" fontSize="sm">
              {errors.backgroundColor.message}
            </Text>
          )}
        </Field.Root>
      </Fieldset.Content>
    </Fieldset.Root>
  );

  return (
    <>
      <Dialog.Root
        lazyMount
        open={open}
        onOpenChange={(e) => {
          setOpen(e.open);
        }}
        placement={'center'}
      >
        <Dialog.Trigger asChild>
          <Button variant={'surface'} p={2} bgColor={'white'}>
            {t('happy-hour.button.edit', 'Edit')}
          </Button>
        </Dialog.Trigger>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title fontSize={'xl'}>{t('top-banner.dialog.update.title', 'Update Top Banner')}</Dialog.Title>
              </Dialog.Header>

              <Dialog.Body>{FieldSet}</Dialog.Body>
              <Dialog.Footer>
                <HStack justifyContent={'space-between'} w={'full'}>
                  <Button
                    loading={deleteLoading}
                    onClick={() => {
                      handleShowDelete();
                    }}
                    p={2}
                    bg={'red.500'}
                    variant={'surface'}
                    color={'white'}
                  >
                    {t('common.button.delete', 'Delete')}
                  </Button>
                  <HStack>
                    <Dialog.ActionTrigger asChild>
                      <Button variant="surface" p={2}>
                        {t('common.button.cancel', 'Cancel')}
                      </Button>
                    </Dialog.ActionTrigger>
                    <Button
                      loading={updateLoading}
                      onClick={() => {
                        void handleSubmit(onSubmit)();
                      }}
                      p={2}
                      bg={'gray.700'}
                      variant={'surface'}
                      color={'white'}
                      type="submit"
                    >
                      {t('common.button.update')}
                    </Button>
                  </HStack>
                </HStack>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <Dialog.Root
        lazyMount
        open={showDeleteConfirm}
        onOpenChange={(e) => {
          setShowDeleteConfirm(e.open);
        }}
        placement={'center'}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title fontSize={'xl'}>{t('top-banner.delete.confirm.title', 'Confirm Delete')}</Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <Text>{t('top-banner.delete.confirm.message', 'Are you sure you want to delete this banner?')}</Text>
              </Dialog.Body>
              <Dialog.Footer>
                <HStack justifyContent={'flex-end'} w={'full'} gap={4}>
                  <Button
                    variant="surface"
                    p={2}
                    onClick={() => {
                      handleCancelDelete();
                    }}
                  >
                    {t('common.button.cancel', 'Cancel')}
                  </Button>
                  <Button
                    loading={deleteLoading}
                    onClick={handleDelete}
                    p={2}
                    bg={'red.500'}
                    variant={'surface'}
                    color={'white'}
                  >
                    {t('common.button.delete', 'Delete')}
                  </Button>
                </HStack>
              </Dialog.Footer>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
}
